import { useState } from 'react';
import type { RunState, AppSettings } from '../run/types';
import { createRunSeed } from '../shared/seed';
import { formatDuration } from '../shared/time';
import { getRunTotalMs } from '../run/runReducer';
import { copyText } from '../shared/clipboard';

type Props = {
  settings: AppSettings;
  resumable: RunState | null;
  history: RunState[];
  onStart: (seed: string) => void;
  onResume: () => void;
  onSettings: () => void;
  onOpenResult: (run: RunState) => void;
};

export function HomeScreen({
  settings,
  resumable,
  history,
  onStart,
  onResume,
  onSettings,
  onOpenResult,
}: Props) {
  const [seed, setSeed] = useState(createRunSeed);
  const [copyStatus, setCopyStatus] = useState('');

  return (
    <main className="home-shell">
      <nav className="top-nav" aria-label="Main navigation">
        <span className="wordmark wordmark--small">GAUNTLE</span>
        <button className="icon-button" onClick={onSettings} aria-label="Open settings">
          ⚙
        </button>
      </nav>

      <section className="hero">
        <p className="eyebrow">Eleven games. Unlimited runs.</p>
        <h1>
          GAUNTLE <span>UNLIMITED</span>
        </h1>
        <p className="hero-copy">
          A deterministic, local puzzle gauntlet. Everything stays in this browser.
        </p>

        {resumable && (
          <button className="resume-card" onClick={onResume}>
            <span>
              <strong>Resume run</strong>
              <small>
                Game {resumable.currentIndex + 1} of {resumable.puzzles.length || 11} ·{' '}
                {resumable.phase}
              </small>
            </span>
            <span aria-hidden="true">→</span>
          </button>
        )}

        <form
          className="new-run-card"
          onSubmit={(event) => {
            event.preventDefault();
            const normalized = seed.trim();
            if (normalized) onStart(normalized);
          }}
        >
          <div>
            <label htmlFor="seed">Run seed</label>
            <p>Use the same seed to replay the same order and puzzles.</p>
          </div>
          <div className="seed-row">
            <input
              id="seed"
              value={seed}
              onChange={(event) => setSeed(event.target.value)}
              maxLength={80}
              autoComplete="off"
            />
            <button
              className="button button--secondary"
              type="button"
              onClick={() => setSeed(createRunSeed())}
            >
              Randomize
            </button>
          </div>
          <div className="mode-summary">
            <span>Mode</span>
            <strong>
              {settings.mode === 'practice'
                ? 'Practice · immediate skips'
                : 'Competitive · timed skips'}
            </strong>
          </div>
          <button className="button button--large" type="submit">
            Start eleven-game run
          </button>
        </form>
      </section>

      <section className="history-section" aria-labelledby="history-title">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Stored on this device</p>
            <h2 id="history-title">Recent results</h2>
          </div>
          <span>{history.length} completed</span>
        </div>
        {history.length === 0 ? (
          <p className="empty-state">Completed runs will appear here.</p>
        ) : (
          <div className="history-list">
            {history.slice(0, 8).map((run) => (
              <div className="history-row" key={run.id}>
                <button className="history-open" onClick={() => onOpenResult(run)}>
                  <span>
                    <strong>{run.seed}</strong>
                    <small>{new Date(run.updatedAt).toLocaleString()}</small>
                  </span>
                  <span>{formatDuration(getRunTotalMs(run))}</span>
                </button>
                <button
                  className="history-copy"
                  aria-label={`Copy seed ${run.seed}`}
                  onClick={async () => {
                    const copied = await copyText(run.seed);
                    setCopyStatus(copied ? `Copied ${run.seed}.` : 'Could not copy seed.');
                  }}
                >
                  Copy seed
                </button>
              </div>
            ))}
            <p className="copy-status" role="status">
              {copyStatus}
            </p>
          </div>
        )}
      </section>
    </main>
  );
}
